import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useTransition,
} from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { CollectiblesDataset } from '@infinex/types';
import {
  EMPTY_COLLECTOR_CRYPT_POINTS_SUMMARY,
  EMPTY_COLLECT_SHIPPING_REDEMPTIONS_SUMMARY,
  EMPTY_GACHAS_SUMMARY,
} from '@infinex/types';
import { useDataset, useOnSessionChange } from '@infinex/user';

import { useAutoHideSpamBalances } from './hooks/useAutoHideSpamBalances';
import { useUnifiedBalances } from './hooks/useUnifiedBalances';
import { useUnifiedBalancesStore } from './stores/useUnifiedBalancesStore';

import { useIsCollectiblePositionsEnabled } from '../../hooks/feature-flag';
import { useInxPrice } from '../../lib';

type BalancesContextValue = {
  manageAssetVisibilityHref: string | null;
};

const EMPTY_COLLECTIBLES_DATASET: CollectiblesDataset = {
  generatedAt: 0,
  cards: [],
  totalValue: '0',
  pricedCardCount: 0,
  unpricedCardCount: 0,
  gachas: EMPTY_GACHAS_SUMMARY,
  collectorCryptPoints: EMPTY_COLLECTOR_CRYPT_POINTS_SUMMARY,
  shipping: EMPTY_COLLECT_SHIPPING_REDEMPTIONS_SUMMARY,
  openOffers: [],
};

const BalancesContext = createContext<BalancesContextValue | null>(null);

type BalancesProviderProps = PropsWithChildren<{
  /**
   * URL to the page for managing asset visibility.
   * `| null` for now because we don't have a managed asset visibility in the extension yet.
   */
  manageAssetVisibilityHref: string | null;
}>;

/**
 * Manages balances store and triggers balances precomputations.
 *
 * Additionally, triggers logic checking for and possibly hiding spam assets.
 */
export function BalancesProvider({
  children,
  manageAssetVisibilityHref,
}: BalancesProviderProps) {
  const [_isPending, startTransition] = useTransition();

  const { clear, datasetTimestamps, precompute } = useUnifiedBalancesStore(
    useShallow(({ clear, precompute, precomputed }) => ({
      clear,
      precompute,
      datasetTimestamps: precomputed?.datasetTimestamps,
    }))
  );

  const { data: chainBalancesData } = useDataset('chainBalances');
  const { data: inxData } = useDataset('inx');
  const { data: perpsData } = useDataset('perps');
  const { data: collectiblesData } = useDataset('collectibles');
  const isCollectiblePositionsEnabled =
    useIsCollectiblePositionsEnabled() ?? false;

  const { inxPrice: inxPriceBigNumber, isLoading: isInxPriceLoading } =
    useInxPrice();
  const inxPrice = !isInxPriceLoading ? inxPriceBigNumber.toNumber() : null;

  useEffect(() => {
    if (!chainBalancesData || !inxData || !perpsData) {
      return;
    }

    if (isCollectiblePositionsEnabled && !collectiblesData) {
      return;
    }

    const enabledCollectiblesData =
      isCollectiblePositionsEnabled && collectiblesData
        ? collectiblesData
        : EMPTY_COLLECTIBLES_DATASET;

    const chainBalancesTimestamp = datasetTimestamps?.chainBalances ?? 0;
    const inxTimestamp = datasetTimestamps?.inx ?? 0;
    const perpsTimestamp = datasetTimestamps?.perps ?? 0;
    const collectiblesTimestamp = datasetTimestamps?.collectibles ?? 0;

    const isStale =
      chainBalancesTimestamp < chainBalancesData.generatedAt ||
      inxTimestamp < (inxData.meta?.lastSyncedAt ?? 0) ||
      perpsTimestamp < perpsData.generatedAt ||
      (isCollectiblePositionsEnabled &&
        collectiblesTimestamp < enabledCollectiblesData.generatedAt);

    if (!isStale) {
      return;
    }

    startTransition(() => {
      precompute(
        chainBalancesData,
        inxData,
        perpsData,
        inxPrice,
        enabledCollectiblesData
      );
    });
  }, [
    chainBalancesData,
    inxData,
    perpsData,
    collectiblesData,
    isCollectiblePositionsEnabled,
    inxPrice,
    datasetTimestamps,
    precompute,
    startTransition,
  ]);

  useOnSessionChange((_, currentSession) => {
    if (!currentSession) {
      clear();
    }
  });

  const unifiedBalances = useUnifiedBalances();

  void useAutoHideSpamBalances([unifiedBalances], manageAssetVisibilityHref);

  const contextValue = useMemo(
    () => ({
      manageAssetVisibilityHref,
    }),
    [manageAssetVisibilityHref]
  );

  return (
    <BalancesContext.Provider value={contextValue}>
      {children}
    </BalancesContext.Provider>
  );
}

export function useBalancesContext(): BalancesContextValue {
  const context = useContext(BalancesContext);

  if (!context) {
    throw new Error(
      'useBalancesContext must be used within a BalancesProvider'
    );
  }

  return context;
}
